<template lang="pug">
.concurrent-group(:class="{ 'concurrent-group--sessions': isConcurrent }")
  //- Non-concurrent: render sessions individually with their own mode
  template(v-if="!isConcurrent")
    ScheduleSessionCard(
      v-for="session in sessions"
      :key="session.key"
      :result="session"
      :mode="cardMode(session)"
      :show-date="showDate"
      @add-session="$emit('add-session', $event)"
      @navigate-detail="$emit('navigate-detail', $event)")

  //- Concurrent: group header + compact cards
  template(v-else)
    .concurrent-group__header(@click="toggle")
      span.concurrent-group__time {{ formatTime(startTime) }}
      .concurrent-group__header-body
        .concurrent-group__icon-box
          img(src="/img/icons/schedule/md/concurrent.svg" alt="" width="24" height="24" aria-hidden="true")
        span.concurrent-group__label Concurrent Sessions
        .concurrent-group__line-break
        span.concurrent-group__count {{ sessions.length }} Session{{ sessions.length !== 1 ? 's' : '' }}
        .concurrent-group__spacer
        span.concurrent-group__toggle-text {{ expanded ? 'Collapse' : 'Expand' }}
        button.concurrent-group__toggle-btn(
          type="button"
          :aria-expanded="String(expanded)"
          aria-label="Toggle concurrent sessions"
          @click.stop="toggle")
          svg.concurrent-group__chevron(
            :class="{ 'concurrent-group__chevron--collapsed': !expanded }"
            viewBox="0 0 16 10" width="16" height="9")
            path(d="M1 9L8 2L15 9" stroke="currentColor" stroke-width="2" fill="none")

    .concurrent-group__cards-wrap(ref="cardsWrap" v-show="expanded" :class="{ 'concurrent-group__cards-wrap--at-bottom': atBottom || !isOverflowing }")
      .concurrent-group__cards(ref="cards" @scroll="onCardsScroll")
        ScheduleSessionCard(
          v-for="session in sessions"
          :key="session.key"
          :result="session"
          mode="compact"
          :show-date="showDate"
          @add-session="$emit('add-session', $event)"
          @navigate-detail="$emit('navigate-detail', $event)")
</template>

<script>
import ScheduleSessionCard from './ScheduleSessionCard.vue';
import { formatTime } from './scheduleTimeUtils.js';
import { isNonSessionItem } from './scheduleResultGrouping.js';

export default {
  name: 'ConcurrentSessionGroup',

  components: {
    ScheduleSessionCard,
  },

  props: {
    sessions: {
      type: Array,
      required: true,
    },
    startTime: {
      type: String,
      required: true,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
    filterResetKey: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      expanded: false,
      atBottom: false,
      isOverflowing: false,
    };
  },

  watch: {
    expanded(val) {
      if (val) {
        this.$nextTick(() => {
          const el = this.$refs.cards;
          if (!el) return;
          this.checkAtBottom();
          this._resizeObserver = new ResizeObserver(() => this.checkAtBottom());
          this._resizeObserver.observe(el);
        });
      } else {
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this.isOverflowing = false;
        this.atBottom = false;
      }
    },
    filterResetKey: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.expanded = true;
        }
      },
    },
  },

  computed: {
    isConcurrent() {
      const regularSessions = this.sessions.filter(s =>
        !isNonSessionItem((s.meta && s.meta.sessionType) || '')
      );
      return regularSessions.length > 1;
    },
  },

  beforeDestroy() {
    this._resizeObserver?.disconnect();
  },

  methods: {
    toggle() {
      this.expanded = !this.expanded;
    },

    onCardsScroll() {
      this.checkAtBottom();
    },

    checkAtBottom() {
      const el = this.$refs.cards;
      if (!el) return;
      const scrollbarWidth = el.offsetWidth - el.clientWidth;
      this.$refs.cardsWrap.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
      this.isOverflowing = el.scrollHeight > el.clientHeight;
      this.atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    },

    cardMode(session) {
      if (isNonSessionItem((session.meta && session.meta.sessionType) || '')) {
        return (session.summary || '').trim() ? 'expanded' : 'simple';
      }
      return 'expanded';
    },

    formatTime,
  },
};
</script>

<style lang="scss" scoped>
@import './tokens';

.concurrent-group {
  margin-bottom: 24px;
  border: 1px solid $color-border;
  border-radius: 8px;
  overflow: hidden;
}

.concurrent-group--sessions {
  border-left: 2px solid $color-border-blue;
}

.concurrent-group__header {
  width: 100%;
  padding: 16px 20px 16px 16px;
  background: white;
  border-radius: 0;
  border-bottom: 1px solid $color-border;
  position: relative;
  min-height: 72px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background: $color-surface-hover;
  }
}

.concurrent-group__time {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  color: $color-primary;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  min-width: 170px;
}

.concurrent-group__header-body {
  margin-left: 194px;
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding: 4px 0;
}

.concurrent-group__icon-box {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background: $color-surface-sunken;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.concurrent-group__label {
  color: $color-text-dark;
  font-size: 17px;
  font-weight: 700;
  white-space: nowrap;
}

.concurrent-group__count {
  height: 24px;
  padding: 0 12px;
  background: $color-bg-secondary;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  color: $color-tag-type-fg;
  display: flex;
  align-items: center;
  white-space: nowrap;
  margin-left: 12px;
}

.concurrent-group__spacer {
  flex: 1;
}

.concurrent-group__toggle-text {
  color: $color-text-secondary;
  font-size: 13px;
  font-weight: 400;
}

.concurrent-group__line-break {
  display: none;
}

.concurrent-group__toggle-btn {
  width: 40px;
  height: 40px;
  padding: 8px;
  background: $color-surface-sunken;
  border-radius: 8px;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $color-primary;
  flex-shrink: 0;

  &:hover {
    background: $color-surface-hover;
    border-color: $color-btn-outline-hover-border;
  }

  &:focus-visible {
    outline: 2px solid $color-primary;
    outline-offset: 2px;
  }
}

.concurrent-group__chevron {
  transition: transform 0.2s ease;

  &--collapsed {
    transform: rotate(180deg);
  }
}

.concurrent-group__cards-wrap {
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: var(--scrollbar-width, 8px);
    height: 90px;
    background: linear-gradient(to bottom, transparent, white 97%);
    pointer-events: none;
    opacity: 1;
    transition: opacity 0.2s ease;
  }

  &--at-bottom::after {
    opacity: 0;
  }
}

.concurrent-group__cards {
  border-radius: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0;
  max-height: 440px;
  overflow-y: auto;
  scrollbar-width: thin;
}

@media (max-width: 1023px) {
  .concurrent-group__header {
    min-height: 0;
  }

  .concurrent-group__time {
    position: static;
    transform: none;
    display: block;
    margin-bottom: 8px;
  }

  .concurrent-group__header-body {
    margin-left: 0;
    flex-wrap: wrap;
  }

  .concurrent-group__spacer {
    display: none;
  }
}

@media (max-width: 991px) {
  .concurrent-group__line-break {
    display: block;
    flex-basis: 100%;
    height: 0;
  }

  .concurrent-group__count {
    margin-left: 0;
  }

  .concurrent-group__spacer {
    display: block;
    flex: 1;
  }
}
</style>
